import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
var _excluded = ["as", "wrapElement"],
    _excluded2 = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { forwardRef } from 'react';
import { hasOwnProperty } from './misc.js';
import { jsx } from 'react/jsx-runtime';

function isRenderProp(children) {
  return typeof children === "function";
}
/**
 * Creates a type-safe component with the `as` prop and `React.forwardRef`.
 *
 * @example
 * import { createComponent } from "ariakit-utils/system";
 *
 * type Props = {
 *   as?: "div";
 *   customProp?: boolean;
 * };
 *
 * const Component = createComponent<Props>(({ customProp, ...props }) => {
 *   return <div {...props} />;
 * });
 *
 * <Component as="button" customProp />
 */


function createComponent(render) {
  var Role = function Role(props, ref) {
    return render(_objectSpread({
      ref: ref
    }, props));
  };

  return /*#__PURE__*/forwardRef(Role);
}
/**
 * Creates a React element that supports the `as` prop, children as a
 * function (render props) and a `wrapElement` function.
 *
 * @example
 * import { createElement } from "ariakit-utils/system";
 *
 * function Component() {
 *   const props = {
 *     as: "button" as const,
 *     children: (htmlProps) => <button {...htmlProps} />,
 *     wrapElement: (element) => <div>{element}</div>,
 *   };
 *   return createElement("div", props);
 * }
 */


function createElement(Type, props) {
  var As = props.as,
      wrapElement = props.wrapElement,
      rest = _objectWithoutProperties(props, _excluded);

  var element;

  if (As && typeof As !== "string") {
    element = /*#__PURE__*/jsx(As, _objectSpread({}, rest));
  } else if (isRenderProp(props.children)) {
    var children = rest.children,
        otherProps = _objectWithoutProperties(rest, _excluded2);

    element = props.children(otherProps);
  } else if (As) {
    element = /*#__PURE__*/jsx(As, _objectSpread({}, rest));
  } else {
    element = /*#__PURE__*/jsx(Type, _objectSpread({}, rest));
  }

  if (wrapElement) {
    return wrapElement(element);
  }

  return element;
}
/**
 * Creates a component hook that accepts props and returns props so they can be
 * passed to a React element.
 *
 * @example
 * import { Options, createHook } from "ariakit-utils/system";
 *
 * type Props = Options<"div"> & {
 *   customProp?: boolean;
 * };
 *
 * const useComponent = createHook<Props>(({ customProp, ...props }) => {
 *   return props;
 * });
 *
 * const props = useComponent({ as: "button", customProp: true });
 */


function createHook(useProps) {
  var useRole = function useRole(props) {
    if (props === void 0) {
      props = {};
    }

    var htmlProps = useProps(props);
    var copy = {};

    for (var prop in htmlProps) {
      if (hasOwnProperty(htmlProps, prop) && htmlProps[prop] !== undefined) {
        // @ts-expect-error
        copy[prop] = htmlProps[prop];
      }
    }

    return copy;
  };

  return useRole;
}

export { createComponent, createElement, createHook };